import { isDealHuntingLandingPage, isSearchPage } from '@helpers/routing';
import React, { useMemo } from 'react';
import { CatalogPageMetadataResponse } from '@interfaces/api/responses/catalog-page-metadata-response';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { CampaignContext } from '@context/campaign.context';
import useBreadcrumbsQuery from '@hooks/product/use-breadcrumbs-query';
import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import CmsImage from '@components/atoms/vc-images/components/cms-image';
import clsx from 'clsx';
import useGuidedSearchQuery from '@hooks/catalog-context/use-guided-search-query';
import CatalogHeaderNavigation from '@components/catalog/catalog-header-navigation/catalog-header-navigation';
import SuggestedSearchQuery from '@components/catalog/product-search/suggested-search-query/suggested-search-query';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';

import styles from './catalog-title-block.module.scss';

interface CatalogTitleBlockProps {
  metadata: CatalogPageMetadataResponse | null;
}

const CatalogTitleBlock: React.FC<CatalogTitleBlockProps> = ({ metadata }) => {
  const onSearchPage = isSearchPage(metadata?.path);
  const searchQuery = useContextSelector(CatalogContext, (v) => v.searchQuery);
  const totalResults = useContextSelector(CatalogContext, (v) => v.totalResults);
  const campaign = useContextSelector(CampaignContext, (v) => v.campaign);
  const { t } = useTranslation();
  const { query } = useRouter();
  const { breadcrumbs } = useBreadcrumbsQuery();
  const { showCarousel } = useGuidedSearchQuery();

  const { title, subtitle, showBreadCrumbs }: { title: string; subtitle: string; showBreadCrumbs: boolean } =
    useMemo(() => {
      if (isDealHuntingLandingPage(query)) {
        return {
          title: t('CATALOG.DEAL_HUNTING_LANDING_PAGE.TITLE'),
          subtitle: t('CATALOG.DEAL_HUNTING_LANDING_PAGE.SUB_TITLE'),
          showBreadCrumbs: false,
        };
      }
      if (onSearchPage) {
        return {
          title: searchQuery,
          subtitle: '',
          showBreadCrumbs: false,
        };
      }
      if (campaign) {
        return {
          title: campaign?.displayTitle ?? '',
          subtitle: campaign?.subtitle ?? '',
          showBreadCrumbs: false,
        };
      }
      return {
        title: metadata?.content?.headline ?? '',
        subtitle: '',
        showBreadCrumbs: true,
      };
    }, [query, onSearchPage, campaign, metadata, t, searchQuery]);

  return (
    <div className={styles.catalogTitleBlock}>
      {breadcrumbs && showBreadCrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          isCatalogPage={true}
          customClass={styles.catalogTitleBlock__breadcrumbs}
        />
      )}
      {campaign?.image && (
        <CmsImage
          src={campaign?.image?.url}
          className={styles.catalogTitleBlock__image}
          wrapperProps={{
            className: styles.catalogTitleBlock__imageWrapper,
          }}
          type="edito"
          width={500}
          height={500}
          alt={campaign?.image?.title}
          data-src={campaign?.image?.filename}
        />
      )}
      <div className={styles.catalogTitleBlock__titleWrapper}>
        <ConditionalWrapper
          condition={breadcrumbs && showBreadCrumbs && breadcrumbs.length > 0}
          wrap={(children) => <h2 className={clsx('vc-title-s', styles.catalogTitleBlock__title)}>{children}</h2>}
          defaultWrapper={(children) => (
            <h1 className={clsx('vc-title-s', styles.catalogTitleBlock__title)}>{children}</h1>
          )}
        >
          {title}
        </ConditionalWrapper>
        <span
          className={styles.catalogTitleBlock__resultsText}
          role="status"
          aria-atomic="true"
          aria-live="polite"
        >
          — {totalResults} {t('CATALOG.FILTERS.RESULTS')}
        </span>
      </div>
      {subtitle && (
        <ConditionalWrapper
          condition={breadcrumbs && showBreadCrumbs && breadcrumbs.length > 0}
          wrap={(children) => <h3 className={styles.catalogTitleBlock__subtitle}>{children}</h3>}
          defaultWrapper={(children) => <h2 className={styles.catalogTitleBlock__subtitle}>{children}</h2>}
        >
          {subtitle}
        </ConditionalWrapper>
      )}
      <SuggestedSearchQuery />
      {showCarousel && (
        <div className={styles.catalogTitleBlock__headerNavigation}>
          <CatalogHeaderNavigation />
        </div>
      )}
    </div>
  );
};

export default CatalogTitleBlock;
